import React from 'react';
import { classNames } from 'react-extras';
import './Hero.scss';
import Img from 'gatsby-image';
import { PrismicHTMLParser } from 'components/PrismicHTMLParser';
import { PrismicLink } from 'components/PrismicLink';
import { Arrow } from 'components/Icons';
import { LrButtonArrow } from 'components/LANDR/LrButtonArrow';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import {
    PrismicTitleFieldType,
    PrismicRichTextFieldType,
    FluidGatsbyImageFieldType,
    PrismicKeyTextFieldType,
    PrismicLinkFieldType,
    BooleanOptionType,
    SliceContext,
} from '../types';
import { SupportedPrismicSlicesEnum } from '../enums';
import { componentDidmountAnimation } from './Animations';

type HeroProps = {
    sliceContext: SliceContext;
    slice: {
        slice_type: SupportedPrismicSlicesEnum.hero;
        primary: {
            hero_title: PrismicTitleFieldType;
            hero_description: PrismicRichTextFieldType;
            hero_alignment: 'Left' | 'Center';
            hero_image: FluidGatsbyImageFieldType;
            hero_cta_label: PrismicKeyTextFieldType;
            hero_cta_link: PrismicLinkFieldType;
            hero_cta_label_secondary: PrismicKeyTextFieldType;
            hero_cta_link_secondary: PrismicLinkFieldType;
            hero_show_arrow: BooleanOptionType;
        };
    };
};

const smoothScroll = (top: number) =>
    window.scrollTo({
        top,
        behavior: 'smooth',
    });

class Hero extends React.Component<HeroProps> {
    heroElement: HTMLDivElement | null;

    scrollPastHero = () => {
        if (this.heroElement) {
            // The navbar is a different height on mobile than it is for desktop
            const navbarHeight = window.innerWidth <= 1024 ? 64 : 80;
            smoothScroll(this.heroElement.offsetTop + this.heroElement.offsetHeight - navbarHeight);
        }
    };

    componentDidMount() {
        componentDidmountAnimation();
    }

    render() {
        const { primary } = this.props.slice;
        const heroAlignmentClass = `Hero--${primary.hero_alignment.toLowerCase()}`;
        const sizes =
            this.props.slice.primary.hero_image &&
            this.props.slice.primary.hero_image.localFile &&
            this.props.slice.primary.hero_image.localFile.childImageSharp &&
            this.props.slice.primary.hero_image.localFile.childImageSharp.sizes;

        const heroClasses: string = classNames('Hero', {
            [heroAlignmentClass]: true,
        });
        const hasBothLinks = !!primary.hero_cta_link_secondary && !!primary.hero_cta_link;
        return (
            <div className={heroClasses} ref={ref => (this.heroElement = ref)}>
                <Grid className="Hero-content">
                    <GridRow horizontalAlign={'h-start'} verticalAlign={'v-center'}>
                        <GridCol colSize={`12`} offsetSize={'0'}>
                            <PrismicHTMLParser className="Hero-content-title">
                                {primary.hero_title.html}
                            </PrismicHTMLParser>

                            <PrismicHTMLParser className="Hero-content-description">
                                {primary.hero_description.html || ''}
                            </PrismicHTMLParser>
                        </GridCol>
                        <GridCol colSize={`12`} offsetSize={'0'} className="Bloc-cta">
                            <div className="Hero-actions">
                                <GridRow
                                    horizontalAlign={'h-center'}
                                    verticalAlign={'v-center'}
                                    className="Row--marginNone"
                                >
                                    <GridCol
                                        mobileColSize={'12'}
                                        offsetSize={'0'}
                                        className="Col--paddingNone"
                                    >
                                        {primary.hero_cta_link && primary.hero_cta_link.url && (
                                            <PrismicLink
                                                link={primary.hero_cta_link}
                                                slice={this.props.sliceContext}
                                            >
                                                <LrButtonArrow size="medium">
                                                    {primary.hero_cta_label}
                                                </LrButtonArrow>
                                            </PrismicLink>
                                        )}
                                    </GridCol>
                                    <GridCol
                                        mobileColSize={'12'}
                                        offsetSize={'0'}
                                        className="paddingNone"
                                    >
                                        {primary.hero_cta_link_secondary &&
                                            primary.hero_cta_link_secondary.url && (
                                                <div className={hasBothLinks ? 'second-link' : ''}>
                                                    <PrismicLink
                                                        link={primary.hero_cta_link_secondary}
                                                        slice={this.props.sliceContext}
                                                    >
                                                        <LrButtonArrow size="medium">
                                                            {primary.hero_cta_label_secondary}
                                                        </LrButtonArrow>
                                                    </PrismicLink>
                                                </div>
                                            )}
                                    </GridCol>
                                </GridRow>
                                {primary.hero_show_arrow === 'Yes' && (
                                    <button
                                        className={
                                            hasBothLinks
                                                ? 'Hero-actions-arrow Two-link'
                                                : 'Hero-actions-arrow'
                                        }
                                        onClick={this.scrollPastHero}
                                        aria-label="Go to next section"
                                    >
                                        <Arrow />
                                    </button>
                                )}
                            </div>
                        </GridCol>
                    </GridRow>
                </Grid>

                {primary.hero_image.dimensions &&
                    (sizes && (
                        <Img
                            className="Hero-background"
                            sizes={sizes}
                            alt={primary.hero_image.alt || ''}
                        />
                    ))}
            </div>
        );
    }
}

export { Hero, HeroProps };
